import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  styled,
  keyframes,
} from '@mui/material';
import { FlagOutlined, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import React, { memo, useCallback, useId, useState, useEffect } from 'react';
import { useActionPopoverContext } from 'op-contexts';
import dayjs from 'op-utils/dayjs';

import LoadingDots from 'op-pages/RO/AiSummary/components/LoadingAnimation';
import RelatedChip from 'op-pages/RO/AiSummary/components/RelatedChip';

import {
  DocumentEditChatMessage,
  ProstateMRIReferralEditChatMessage,
  isDocumentType,
} from 'op-pages/RO/AiSummary/components/EditChatMessage';
import {
  timestampCaptionSx,
  userMessageBubbleSx,
  messageContainerSx,
} from 'op-pages/RO/AiSummary/components/Chat.styles';
import { CitationProps } from 'op-pages/RO/AiSummary/components/CitationPanel';
import { ChatRequest } from 'op-pages/RO/AiSummary/components/Interface';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { sanitizeNote } from 'op-pages/RO/Notes/sanitizeNote';
import { ChatContentHTML } from './ChatMessage.styles';

// ---------------------------------------------------------------------------
// ThinkingAccordion – collapsible "thinking" box for model reasoning
// ---------------------------------------------------------------------------

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ThinkingSpinner = styled('div')(({ theme }) => ({
  width: 14,
  height: 14,
  borderRadius: '50%',
  border: `2px solid ${theme.palette.divider}`,
  borderTopColor: theme.palette.primary.main,
  animation: `${spin} 0.8s linear infinite`,
  flexShrink: 0,
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  '&.MuiAccordion-root': {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px !important',
    boxShadow: 'none',
    backgroundColor: theme.palette.action.hover,
    '&::before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  '&.MuiAccordionSummary-root': {
    minHeight: 36,
    padding: `0 ${theme.spacing(1.5)}`,
    '&.Mui-expanded': { minHeight: 36 },
  },
  '& .MuiAccordionSummary-content': {
    margin: `${theme.spacing(0.75)} 0`,
    '&.Mui-expanded': { margin: `${theme.spacing(0.75)} 0` },
  },
}));

const StatusBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.action.hover,
}));

interface ThinkingAccordionProps {
  reasoningText?: string;
  statusText?: string;
}

const ThinkingAccordion: React.FC<ThinkingAccordionProps> = ({ reasoningText, statusText }) => {
  const [visible, setVisible] = useState(false);
  const detailsId = useId();

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const hasReasoningContent = Boolean(reasoningText);
  const label = hasReasoningContent ? 'Thinking' : statusText || 'Processing...';

  return (
    <Collapse in={visible} timeout={300}>
      {hasReasoningContent ? (
        <StyledAccordion disableGutters>
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            aria-controls={detailsId}>
            <Stack direction="row" alignItems="center" gap={1}>
              <ThinkingSpinner />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                  animation: `${pulse} 1.5s ease-in-out infinite`,
                }}>
                Thinking
              </Typography>
            </Stack>
          </StyledAccordionSummary>
          <AccordionDetails id={detailsId} sx={{ pt: 0, pb: 1, px: 1.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                whiteSpace: 'pre-wrap',
                pl: 3.5,
                lineHeight: 1.6,
              }}>
              {reasoningText}
            </Typography>
          </AccordionDetails>
        </StyledAccordion>
      ) : (
        <StatusBar>
          <ThinkingSpinner />
          <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
        </StatusBar>
      )}
    </Collapse>
  );
};

// ---------------------------------------------------------------------------

interface ChatMessageProps {
  message: ChatRequest;
  index: number;
  timezone: string;
  hoveredMessageIndex: number | null;
  selectedCitation: string | null;
  isPopoverLayout?: boolean;
  onMessageHover: (index: number | null) => void;
  onCitationClick: (citation: CitationProps, citations: CitationProps[], citationIndex: number) => void;
  onFeedbackClick: (message: ChatRequest) => void;
  onEditClick?: (type: string, message: ChatRequest) => void;
  hasDocumentGenerationFeature?: boolean;
}

/**
 * Chat message component - memoized to prevent re-renders during streaming
 * Only re-renders when message content, hover state, or selection changes
 */
export const ChatMessage: React.FC<ChatMessageProps> = memo(
  ({
    message,
    index,
    timezone,
    hoveredMessageIndex,
    selectedCitation,
    isPopoverLayout = false,
    onMessageHover,
    onCitationClick,
    onFeedbackClick,
    onEditClick,
    hasDocumentGenerationFeature = false,
  }) => {
    const { popoverContainerEl } = useActionPopoverContext();
    const handleMouseEnter = useCallback(() => onMessageHover(index), [onMessageHover, index]);
    const handleMouseLeave = useCallback(() => onMessageHover(null), [onMessageHover]);
    const handleFeedbackClick = useCallback(() => onFeedbackClick(message), [onFeedbackClick, message]);
    const handleProstateMRIEdit = useCallback(
      () => onEditClick?.('prostate_mri_referral', message),
      [onEditClick, message],
    );
    const handleDocumentEdit = useCallback(() => onEditClick?.(message.type, message), [onEditClick, message]);

    const isHovered = hoveredMessageIndex === index;
    return (
      <>
        {/* User Request */}
        <Stack direction="column" alignItems="flex-end" sx={messageContainerSx}>
          <Typography variant="body1" sx={userMessageBubbleSx}>
            {message.text}
          </Typography>
          {message.createdAt && (
            <Typography variant="caption" sx={timestampCaptionSx}>
              {dayjs.utc(message.createdAt).tz(timezone).format(CurrentAppConfig.AISummary.lastUpdatedDateTimeFormat)}
            </Typography>
          )}
        </Stack>

        {/* Assistant Response */}
        <Stack
          direction="column"
          justifyContent="flex-start"
          sx={messageContainerSx}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          {message.response === '' ? (
            <>
              {message.reasoningText || message.statusText ? (
                <ThinkingAccordion reasoningText={message.reasoningText} statusText={message.statusText} />
              ) : (
                <LoadingDots />
              )}
            </>
          ) : (
            <>
              <Typography component="div" variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.type === 'prostate_mri_referral' ? (
                  <ProstateMRIReferralEditChatMessage message={message} onEditClick={handleProstateMRIEdit} />
                ) : isDocumentType(message.type) && hasDocumentGenerationFeature && !isPopoverLayout ? (
                  <DocumentEditChatMessage
                    message={message}
                    documentType={message.type}
                    onEditClick={handleDocumentEdit}
                  />
                ) : isDocumentType(message.type) && hasDocumentGenerationFeature && isPopoverLayout ? (
                  <Stack direction="column" gap={1}>
                    <ChatContentHTML $format={true} dangerouslySetInnerHTML={sanitizeNote(message.response, true)} />
                    <Typography variant="body2" color="text.secondary">
                      Open the full Ask page to view and edit the document.
                    </Typography>
                  </Stack>
                ) : (
                  <ChatContentHTML $format={true} dangerouslySetInnerHTML={sanitizeNote(message.response, true)} />
                )}
              </Typography>
              {(message.responseCreatedAt || message.createdAt) && (
                <Typography variant="caption" sx={timestampCaptionSx}>
                  {dayjs
                    .utc(message.responseCreatedAt || message.createdAt)
                    .tz(timezone)
                    .format(CurrentAppConfig.AISummary.lastUpdatedDateTimeFormat)}
                </Typography>
              )}
              {message?.citations && message.citations.length > 0 && (
                <Stack
                  direction={isPopoverLayout ? 'column' : 'row'}
                  alignItems={isPopoverLayout ? 'start' : 'center'}
                  flexWrap="wrap"
                  gap={1}
                  sx={{ paddingTop: '16px' }}>
                  <Typography variant="body2" color="text.secondary">
                    Related:
                  </Typography>
                  {message.citations.map((citation, citationIndex) => (
                    <RelatedChip
                      key={citation.id}
                      label={citation.title || ''}
                      status={citation.status}
                      icon={citation.icon as React.ReactElement | undefined}
                      onClick={() => onCitationClick(citation, message.citations, citationIndex)}
                      selected={citation.id === selectedCitation}
                      snippets={citation.snippets || []}
                      variant={citation.id === selectedCitation ? 'filled' : 'outlined'}
                      size="small"
                    />
                  ))}
                </Stack>
              )}
              <Box sx={{ alignSelf: 'flex-start', mt: 1 }}>
                <Tooltip
                  title="Flag an issue"
                  placement="right"
                  PopperProps={{
                    disablePortal: Boolean(popoverContainerEl),
                    container: popoverContainerEl ?? undefined,
                  }}>
                  <IconButton
                    size="small"
                    color="primary"
                    sx={{
                      marginTop: '8px',
                      width: '28px',
                      height: '28px',
                      visibility: isHovered ? 'visible' : 'hidden',
                    }}
                    aria-label="Flag an issue with this AI response"
                    onClick={handleFeedbackClick}>
                    <FlagOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </Stack>
      </>
    );
  },
  // Custom comparison - only re-render when necessary
  (prevProps, nextProps) => {
    // Always re-render if message content changed (for streaming)
    if (prevProps.message.response !== nextProps.message.response) return false;
    if (prevProps.message.text !== nextProps.message.text) return false;
    if (prevProps.message.citations !== nextProps.message.citations) return false;
    if (prevProps.message.responseCreatedAt !== nextProps.message.responseCreatedAt) return false;
    // Re-render if statusText or reasoningText changed (for status/thinking updates during processing)
    if (prevProps.message.statusText !== nextProps.message.statusText) return false;
    if (prevProps.message.reasoningText !== nextProps.message.reasoningText) return false;

    // Re-render if hover state changed for this message
    const wasHovered = prevProps.hoveredMessageIndex === prevProps.index;
    const isHovered = nextProps.hoveredMessageIndex === nextProps.index;
    if (wasHovered !== isHovered) return false;

    // Re-render if selected citation changed and this message has citations
    if (prevProps.selectedCitation !== nextProps.selectedCitation && nextProps.message.citations?.length) return false;
    // Otherwise, skip re-render
    return true;
  },
);
